import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const DEFAULT_MAX_VISIBLE_PAGES = 5;

interface PaginationProps {
    page: number;
    pageSize: number;
    totalCount: number;
    isLoading: boolean;
    hasMore: boolean;
    onPageChange: (page: number) => void;
    maxVisiblePages?: number;
}

function getVisiblePages(
    currentPage: number,
    totalPages: number,
    maxVisiblePages: number,
): number[] {
    if (totalPages <= maxVisiblePages) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const halfWindow = Math.floor(maxVisiblePages / 2);
    const rawStart = currentPage - halfWindow;
    const maxStart = totalPages - maxVisiblePages + 1;
    const start = Math.min(Math.max(1, rawStart), maxStart);
    return Array.from({ length: maxVisiblePages }, (_, i) => start + i);
}

export function Pagination({
    page,
    pageSize,
    totalCount,
    isLoading,
    hasMore,
    onPageChange,
    maxVisiblePages = DEFAULT_MAX_VISIBLE_PAGES,
}: PaginationProps) {
    const { t } = useTranslation();
    const totalPages = Math.ceil(totalCount / pageSize);
    const safeMaxVisiblePages =
        Number.isInteger(maxVisiblePages) && maxVisiblePages > 0
            ? maxVisiblePages
            : DEFAULT_MAX_VISIBLE_PAGES;
    const safePage = Math.min(Math.max(1, page), Math.max(totalPages, 1));
    const pages = getVisiblePages(safePage, totalPages, safeMaxVisiblePages);
    const isPrevDisabled = safePage <= 1 || isLoading;
    const isNextDisabled = !hasMore || isLoading;

    return (
        <div className="flex flex-row items-center gap-4 h-8">
            <button
                type="button"
                onClick={() => onPageChange(safePage - 1)}
                disabled={isPrevDisabled}
                className={cn(
                    'flex flex-row items-center justify-center gap-1.5 h-8 px-2 rounded-[5px] transition-colors',
                    isPrevDisabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:bg-grey-550',
                )}
                aria-label={t('ariaLabels.previousPage')}
            >
                <CaretLeftIcon
                    className="w-4 h-4 text-grey-200 shrink-0"
                    weight="regular"
                />
                <span className="text-[14px] leading-5 font-medium text-grey-200">
                    {t('button.previous')}
                </span>
            </button>

            <div className="flex flex-row items-center gap-3 h-8">
                {pages.map((pageNum) => (
                    <button
                        key={pageNum}
                        type="button"
                        onClick={() => onPageChange(pageNum)}
                        disabled={isLoading}
                        className={cn(
                            'flex flex-row justify-center items-center min-w-[24px] h-8 px-1 rounded-[5px] text-[14px] leading-5 font-medium transition-colors',
                            pageNum === safePage
                                ? 'text-white'
                                : 'text-grey-200 hover:bg-grey-550',
                            isLoading && 'cursor-not-allowed',
                        )}
                    >
                        {pageNum}
                    </button>
                ))}
            </div>

            <button
                type="button"
                onClick={() => onPageChange(safePage + 1)}
                disabled={isNextDisabled}
                className={cn(
                    'flex flex-row items-center justify-center gap-1.5 h-8 px-2 rounded-[5px] transition-colors',
                    isNextDisabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:bg-grey-550',
                )}
                aria-label={t('ariaLabels.nextPage')}
            >
                <span className="text-[14px] leading-5 font-medium text-grey-200">
                    {t('button.next')}
                </span>
                <CaretRightIcon
                    className="w-4 h-4 text-grey-200 shrink-0"
                    weight="regular"
                />
            </button>
        </div>
    );
}
